import React, { useState, useEffect, useRef } from 'react';
import { 
  Package, QrCode, Truck, User, Clock, MapPin, Phone, Mail, 
  Maximize2, Minimize2, Printer, RefreshCw, Camera, Check, X,
  Navigation, Image as ImageIcon, ChevronDown, ChevronUp, Search
} from 'lucide-react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import BackButton from '../components/BackButton';
import QRCode from 'qrcode';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const OrderPickupAdmin = ({ token: propToken }) => {
  const token = propToken || localStorage.getItem('auth_token');
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [enlargedOrder, setEnlargedOrder] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [qrCodes, setQrCodes] = useState({});

  useEffect(() => {
    fetchPickupOrders();
  }, []);

  useEffect(() => {
    // Generate QR codes for all orders
    orders.forEach(async (order) => {
      if (!qrCodes[order.id]) {
        try {
          const qrDataUrl = await QRCode.toDataURL(order.qr_code_data || `UPLANDS-PICKUP-${order.id}`, {
            width: 200,
            margin: 2,
            color: { dark: '#000000', light: '#ffffff' }
          });
          setQrCodes(prev => ({ ...prev, [order.id]: qrDataUrl }));
        } catch (err) {
          console.error('QR generation error:', err);
        }
      }
    });
  }, [orders]);

  const fetchPickupOrders = async () => {
    try {
      const response = await fetch(`${API_URL}/api/pickup/admin/orders`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setOrders(data.orders || []);
      } else {
        toast.error('Failed to fetch pickup orders');
      }
    } catch (error) {
      toast.error('Error fetching orders');
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = (order) => {
    const printWindow = window.open('', '_blank');
    const qrCode = qrCodes[order.id];
    
    printWindow.document.write(`
      <html>
        <head>
          <title>Order #${order.order_number || order.id.slice(-8)} - Pickup</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; }
            .header { text-align: center; margin-bottom: 20px; }
            .qr-code { text-align: center; margin: 20px 0; }
            .qr-code img { width: 250px; height: 250px; }
            .order-info { margin: 20px 0; }
            .order-info p { margin: 5px 0; }
            .products { margin-top: 20px; }
            .product { padding: 5px 0; border-bottom: 1px solid #eee; }
            @media print { body { padding: 10px; } }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>UPLANDS FOOD MARKET</h1>
            <h2>ORDER PICKUP</h2>
          </div>
          <div class="qr-code">
            <img src="${qrCode}" alt="QR Code" />
            <p><strong>Order #${order.order_number || order.id.slice(-8).toUpperCase()}</strong></p>
          </div>
          <div class="order-info">
            <p><strong>Customer:</strong> ${order.customer_name || 'N/A'}</p>
            <p><strong>Phone:</strong> ${order.customer_phone || 'N/A'}</p>
            <p><strong>Type:</strong> ${order.order_type === 'pickup' ? 'Customer Pickup' : 'Delivery'}</p>
            ${order.driver_name ? `<p><strong>Driver:</strong> ${order.driver_name}</p>` : ''}
          </div>
          <div class="products">
            <h3>Products (${order.items?.length || 0} items)</h3>
            ${(order.items || []).map(item => `
              <div class="product">
                <strong>${item.name}</strong> x ${item.quantity}
                ${item.price ? ` - $${(item.price * item.quantity).toFixed(2)}` : ''}
              </div>
            `).join('')}
          </div>
          <div style="margin-top: 20px; text-align: center;">
            <p><strong>Total: $${order.total?.toFixed(2) || '0.00'}</strong></p>
            <p style="color: #666; font-size: 12px;">Created: ${new Date(order.created_at).toLocaleString()}</p>
          </div>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('en-US', {
      month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  const getStatusColor = (status) => {
    const colors = {
      'ready_for_pickup': 'bg-green-100 text-green-700',
      'packed': 'bg-blue-100 text-blue-700',
      'ready': 'bg-emerald-100 text-emerald-700',
      'waiting_for_driver': 'bg-orange-100 text-orange-700',
      'waiting_for_customer': 'bg-purple-100 text-purple-700'
    };
    return colors[status] || 'bg-gray-100 text-gray-700';
  };

  const filteredOrders = orders.filter(order => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      order.id?.toLowerCase().includes(query) ||
      order.order_number?.toLowerCase().includes(query) ||
      order.customer_name?.toLowerCase().includes(query) ||
      order.customer_phone?.includes(query)
    );
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-gray-200 rounded w-48"></div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {[1,2,3].map(i => <div key={i} className="h-64 bg-gray-200 rounded"></div>)}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6" data-testid="order-pickup-admin">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <BackButton />
            <div>
              <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                <Package className="w-7 h-7 text-emerald-600" />
                ORDER PICKUP
              </h1>
              <p className="text-gray-500 text-sm">Orders ready for pickup by customers or drivers</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Badge variant="secondary" className="text-lg px-3 py-1">
              {orders.length} Orders Ready
            </Badge>
            <Button variant="outline" onClick={fetchPickupOrders}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>
        </div>

        {/* Search */}
        <div className="mb-6">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              placeholder="Search by order #, customer name, or phone..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>

        {/* Orders Grid */}
        {filteredOrders.length === 0 ? (
          <Card className="p-12 text-center">
            <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-700">No Orders Ready for Pickup</h3>
            <p className="text-gray-500 mt-2">Orders will appear here when they are ready</p>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredOrders.map((order) => (
              <Card 
                key={order.id}
                className="p-4 hover:shadow-lg transition-shadow cursor-pointer"
                onDoubleClick={() => setEnlargedOrder(order)}
                data-testid={`pickup-order-${order.id}`}
              >
                {/* Header */}
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <p className="text-lg font-bold text-gray-900">
                      #{order.order_number || order.id.slice(-8).toUpperCase()}
                    </p>
                    <Badge className={getStatusColor(order.status)}>
                      {order.status?.replace(/_/g, ' ')}
                    </Badge>
                  </div>
                  <Badge variant="outline" className={order.order_type === 'pickup' ? 'text-purple-600' : 'text-blue-600'}>
                    {order.order_type === 'pickup' ? 'Customer Pickup' : 'Delivery'}
                  </Badge>
                </div>

                {/* QR Code */}
                <div className="flex justify-center my-4">
                  {qrCodes[order.id] ? (
                    <img 
                      src={qrCodes[order.id]} 
                      alt="QR Code" 
                      className="w-32 h-32 border rounded-lg"
                    />
                  ) : (
                    <div className="w-32 h-32 bg-gray-100 rounded-lg flex items-center justify-center">
                      <QrCode className="w-12 h-12 text-gray-400" />
                    </div>
                  )}
                </div>

                {/* Customer Info */}
                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2">
                    <User className="w-4 h-4 text-gray-400" />
                    <span className="font-medium">{order.customer_name || 'Unknown Customer'}</span>
                  </div>
                  {order.customer_phone && (
                    <div className="flex items-center gap-2">
                      <Phone className="w-4 h-4 text-gray-400" />
                      <span>{order.customer_phone}</span>
                    </div>
                  )}
                  {order.driver_name && (
                    <div className="flex items-center gap-2 text-blue-600">
                      <Truck className="w-4 h-4" />
                      <span>Driver: {order.driver_name}</span>
                    </div>
                  )}
                </div>

                {/* Products Summary */}
                <div className="mt-3 pt-3 border-t">
                  <p className="text-sm text-gray-600">
                    <strong>{order.items?.length || 0}</strong> items • 
                    <strong> ${order.total?.toFixed(2) || '0.00'}</strong>
                  </p>
                  <p className="text-xs text-gray-400 mt-1">
                    <Clock className="w-3 h-3 inline mr-1" />
                    {formatDate(order.created_at)}
                  </p>
                </div>

                {/* Actions */}
                <div className="mt-3 flex gap-2">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="flex-1"
                    onClick={(e) => { e.stopPropagation(); setEnlargedOrder(order); }}
                  >
                    <Maximize2 className="w-4 h-4 mr-1" />
                    Enlarge
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="flex-1"
                    onClick={(e) => { e.stopPropagation(); handlePrint(order); }}
                  >
                    <Printer className="w-4 h-4 mr-1" />
                    Print
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        )}

        {/* Enlarged Order Modal */}
        {enlargedOrder && (
          <div 
            className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50"
            onClick={() => setEnlargedOrder(null)}
          >
            <Card 
              className="w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-white"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6">
                {/* Header */}
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h2 className="text-3xl font-bold text-gray-900">
                      ORDER #{enlargedOrder.order_number || enlargedOrder.id.slice(-8).toUpperCase()}
                    </h2>
                    <div className="flex gap-2 mt-2">
                      <Badge className={`text-lg px-3 py-1 ${getStatusColor(enlargedOrder.status)}`}>
                        {enlargedOrder.status?.replace(/_/g, ' ')}
                      </Badge>
                      <Badge variant="outline" className="text-lg px-3 py-1">
                        {enlargedOrder.order_type === 'pickup' ? 'Customer Pickup' : 'Delivery'}
                      </Badge>
                    </div>
                  </div>
                  <Button variant="ghost" onClick={() => setEnlargedOrder(null)}>
                    <X className="w-6 h-6" />
                  </Button>
                </div>

                {/* Large QR Code */}
                <div className="flex justify-center mb-6">
                  {qrCodes[enlargedOrder.id] ? (
                    <div className="text-center">
                      <img 
                        src={qrCodes[enlargedOrder.id]} 
                        alt="QR Code" 
                        className="w-64 h-64 border-4 border-gray-200 rounded-xl mx-auto"
                      />
                      <p className="mt-2 text-sm text-gray-500">Scan to verify pickup</p>
                    </div>
                  ) : (
                    <div className="w-64 h-64 bg-gray-100 rounded-xl flex items-center justify-center">
                      <QrCode className="w-24 h-24 text-gray-400" />
                    </div>
                  )}
                </div>

                {/* Customer & Driver Info */}
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <h3 className="font-semibold text-gray-700 mb-2 flex items-center gap-2">
                      <User className="w-5 h-5" />
                      Customer
                    </h3>
                    <p className="font-medium text-lg">{enlargedOrder.customer_name || 'N/A'}</p>
                    {enlargedOrder.customer_phone && (
                      <p className="text-gray-600">{enlargedOrder.customer_phone}</p>
                    )}
                    {enlargedOrder.customer_email && (
                      <p className="text-gray-600 text-sm">{enlargedOrder.customer_email}</p>
                    )}
                  </div>
                  {enlargedOrder.order_type !== 'pickup' && (
                    <div className="p-4 bg-blue-50 rounded-lg">
                      <h3 className="font-semibold text-blue-700 mb-2 flex items-center gap-2">
                        <Truck className="w-5 h-5" />
                        Driver
                      </h3>
                      {enlargedOrder.driver_name ? (
                        <>
                          <p className="font-medium text-lg">{enlargedOrder.driver_name}</p>
                          {enlargedOrder.driver_phone && (
                            <p className="text-gray-600">{enlargedOrder.driver_phone}</p>
                          )}
                        </>
                      ) : (
                        <p className="text-gray-500">Not assigned yet</p>
                      )}
                    </div>
                  )}
                </div>

                {/* Products */}
                <div className="mb-6">
                  <h3 className="font-semibold text-gray-700 mb-3">Products ({enlargedOrder.items?.length || 0} items)</h3>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {(enlargedOrder.items || []).map((item, index) => (
                      <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center gap-3">
                          {item.image && (
                            <img src={item.image} alt={item.name} className="w-12 h-12 rounded object-cover" />
                          )}
                          <div>
                            <p className="font-medium">{item.name}</p>
                            <p className="text-sm text-gray-500">Qty: {item.quantity}</p>
                          </div>
                        </div>
                        <p className="font-semibold">${(item.price * item.quantity).toFixed(2)}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Total */}
                <div className="p-4 bg-emerald-50 rounded-lg flex items-center justify-between">
                  <span className="text-xl font-semibold text-emerald-700">Total</span>
                  <span className="text-3xl font-bold text-emerald-700">${enlargedOrder.total?.toFixed(2) || '0.00'}</span>
                </div>

                {/* Actions */}
                <div className="mt-6 flex gap-3">
                  <Button 
                    className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                    onClick={() => handlePrint(enlargedOrder)}
                  >
                    <Printer className="w-5 h-5 mr-2" />
                    Print Order
                  </Button>
                  <Button variant="outline" className="flex-1" onClick={() => setEnlargedOrder(null)}>
                    Close
                  </Button>
                </div>
              </div>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

export default OrderPickupAdmin;
